import { useTranslation } from 'react-i18next';
import { Event } from '../lib/types';

interface EventLinksProps {
  event: Event;
  isPaidHost?: boolean;
}

interface LinkItem {
  href: string;
  displayUrl: string;
  title: string;
}

export default function EventLinks({ event, isPaidHost = false }: EventLinksProps) {
  const { t } = useTranslation();

  // URL cleaning function similar to PHP setAndCleanUrl
  const cleanAndFormatUrl = (url: string | null | undefined): string => {
    if (!url || url === '') {
      return '';
    }

    // Basic validation - if it looks like plain text rather than a URL, return empty
    if (!/^https?:\/\/|^www\.|^\w+\.\w+/.test(url.trim()) && url.includes(' ')) {
      return ''; // Likely plain text, not a URL
    }

    // Clean up text before http:// or https://
    let cleanedUrl = url.replace(/.*(?=https?:\/\/)/, '');

    // Check if the URL starts with http, if not, prepend http://
    if (cleanedUrl && !/^https?:\/\//.test(cleanedUrl)) {
      cleanedUrl = 'http://' + cleanedUrl;
    }

    // Final validation - try to create URL to ensure it's valid
    try {
      new URL(cleanedUrl);
      return cleanedUrl;
    } catch {
      return ''; // Invalid URL, return empty
    }
  };

  // Inject Amazon affiliate tag (for all hosts) if missing
  const appendAmazonTagIfNeeded = (url: string): string => {
    try {
      const u = new URL(url);
      const isAmazon = /(^|\.)amazon\./i.test(u.hostname);
      if (!isAmazon) return url;
      // If tag already present, leave it
      if (u.searchParams.has('tag')) return url;
      u.searchParams.append('tag', 'invitd0b-20');
      return u.toString();
    } catch {
      return url;
    }
  };

  // Hide the affiliate tag in the displayed URL (do not modify the href)
  const stripAffiliateTagForDisplay = (url: string): string => {
    try {
      const u = new URL(url);
      // Remove only the 'tag' parameter from the query for display
      if (u.searchParams.has('tag')) {
        u.searchParams.delete('tag');
      }
      return u.toString();
    } catch {
      return url;
    }
  };

  const allLinks: (LinkItem | null)[] = [
    event.event_link ? (() => {
      const cleaned = cleanAndFormatUrl(event.event_link);
      const href = appendAmazonTagIfNeeded(cleaned);
      return {
        href,
        displayUrl: stripAffiliateTagForDisplay(href),
        title: event.event_link_title || 'Event Link'
      } as LinkItem;
    })() : null,
    event.event_link2 ? (() => {
      const cleaned = cleanAndFormatUrl(event.event_link2);
      const href = appendAmazonTagIfNeeded(cleaned);
      return {
        href,
        displayUrl: stripAffiliateTagForDisplay(href),
        title: event.event_link2_title || 'Event Link 2'
      } as LinkItem;
    })() : null,
    event.event_link3 ? (() => {
      const cleaned = cleanAndFormatUrl(event.event_link3);
      const href = appendAmazonTagIfNeeded(cleaned);
      return {
        href,
        displayUrl: stripAffiliateTagForDisplay(href),
        title: event.event_link3_title || 'Event Link 3'
      } as LinkItem;
    })() : null
  ];

  const links: LinkItem[] = allLinks.filter((link): link is LinkItem => link !== null && link.href !== '').slice(0, 3);

  if (links.length === 0) return null;

  const getFaviconUrl = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
      return null;
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 shadow">
      <div className="flex items-center space-x-2 mb-4">
        <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900">{t('event.importantLinks', { defaultValue: 'Important Links' })}</h3>
      </div>
      <div className="space-y-3">
        {links.map((link, index) => (
          <a
            key={index}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors border border-gray-200"
          >
            <div className="flex items-center space-x-3">
              {/* Favicon */}
              <div className="flex-shrink-0">
                <img
                  src={getFaviconUrl(link.href) || undefined}
                  alt=""
                  className="w-5 h-5 rounded"
                  onError={(e) => {
                    // Hide the image if it fails to load
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              
              {/* Link Content */}
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 font-medium truncate">{link.title}</p>
                <p className="text-sm text-gray-500 truncate">{link.displayUrl}</p>
              </div>
              
              {/* External Link Icon */}
              <div className="flex-shrink-0">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}